import { Table, Typography } from "antd";
import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from "react";
import axios from "api/axios";
import { useDpatIndicatorDataSource } from "hooks/useDpatIndicatorDataSource";
import DpatSubIndicatorSection from "../setup/DpatSubIndicatorSection";
import DpatIndicatorCmsIntro from "../setup/DpatIndicatorCmsIntro";
import MaxScoreDisplay from "../setup/MaxScoreDisplay";
import ActualScoreDisplay from "../setup/ActualScoreDisplay";
import IndicatorScoreSection from "../setup/IndicatorScoreSection";
import Comment from "components/Comments";
import {
  formatDataGeneral,
  getAttributeValue,
  getFileLinkIfExist,
  getYearFromDateValue,
} from "utils/utils";
import { transformDpatTableRows } from "utils/renderDpatEvidenceLink";
import { isDpatIncidentYear, isDpatScoreRelaxationExempt } from "utils/dpatScoreRelaxation";
import { buildSearchableColumns, getDpatTablePagination } from "utils/dpatSearchableTable";

const PWD_PROGRAM = "OiDekszWx2p";
const DOCUMENTS_HUB_PROGRAM = "SERtHvYVHRT";
const PWD_DETAIL_STAGE = "aPrb3k7a8eV";
const NHIS_ELEMENT = "UfmLydzXyEP";
const IGA_ELEMENT = "eWAPdFz4S8r";
const CAPACITY_BUILDING_ELEMENT = "Cj03jpBNbpB";
const MONITORING_REPORT_FILE = "MjPmcbueEdU";
const PWD_PROGRESS_REPORT_TYPE = "Annual Progress Reports on PWD";

function buildFileUrl(eventId, dataElementId) {
  return `https://dddp.gov.gh/api/events/files?eventUid=${eventId}&dataElementUid=${dataElementId}`;
}

function formatDate(value) {
  if (!value || value === "N/A") {
    return "N/A";
  }
  return String(value).split("T")[0];
}

function getIncidentDateRaw(pwd) {
  const enrollment = pwd.enrollments?.find((entry) => entry.status === "ACTIVE") || pwd.enrollments?.[0];
  return enrollment?.occurredAt || enrollment?.enrolledAt || null;
}

function getIncidentDate(pwd) {
  return formatDate(getIncidentDateRaw(pwd));
}

function isIncidentOnOrBeforeSelectedYear(pwd, selectedYear) {
  const yearNum = Number(selectedYear);
  if (!Number.isFinite(yearNum)) {
    return false;
  }

  const incidentYear = getYearFromDateValue(getIncidentDateRaw(pwd));
  if (isDpatScoreRelaxationExempt()) {
    return isDpatIncidentYear(incidentYear, yearNum);
  }
  return incidentYear != null && incidentYear <= yearNum;
}

function getNhisEnrolmentDate(pwd, reports) {
  const currentReport = reports.find(
    (report) => report.trackedEntity === pwd.trackedEntity && report.programStage === PWD_DETAIL_STAGE,
  );

  if (!currentReport) {
    return "N/A";
  }

  const hasNhis = currentReport.dataValues?.some(
    (value) => value.dataElement === NHIS_ELEMENT && value.value === "true",
  );

  if (!hasNhis) {
    return "N/A";
  }

  return formatDate(currentReport.scheduledAt || currentReport.occurredAt);
}

function calculatePercentage(part, total) {
  const totalNum = parseFloat(total);
  const partNum = parseFloat(part);

  if (Number.isNaN(totalNum) || Number.isNaN(partNum) || totalNum === 0) {
    return 0;
  }

  return ((partNum / totalNum) * 100).toFixed(2);
}

const PWDService = forwardRef(({
  year,
  districtId,
  hideComment,
  savedAssessmentData,
  assessmentStartLoading,
  isAssessmentStarted,
}, ref) => {
  const [scorei, setScorei] = useState(0);
  const [scoreii, setScoreii] = useState(0);
  const [scoreiii, setScoreiii] = useState(0);
  const [pwdData, setPwdData] = useState({ pwd: [], iga: [], cb: [] });
  const [pwdList, setPwdList] = useState([]);
  const [progressReport, setProgressReport] = useState([]);
  const [maxScore, setMaxScore] = useState(4);

  const { Title } = Typography;

  useImperativeHandle(ref, () => ({
    getData: () => ({
      indicator: "SDI4",
      area: "Social Protection, Gender and Nutrition",
      maxScore,
      pwdData,
      pwdList,
      progressReport,
      scorei,
      scoreii,
      scoreiii,
    }),
  }));

  const DPAT_SLUG = "sdi4.0-4.3";

  const loadFromDhis2 = useCallback(() => {
    const pwdRequest = axios.get(
      `/tracker/trackedEntities?orgUnit=${districtId}&program=${PWD_PROGRAM}&pageSize=5000&fields=trackedEntity,attributes,enrollments[enrolledAt,occurredAt,status]`,
    );
    const documentRequest = axios.get(
      `/tracker/trackedEntities?orgUnit=${districtId}&program=${DOCUMENTS_HUB_PROGRAM}&startDate=${year}-01-01&endDate=${year}-12-31&pageSize=5000`,
    );

    Promise.all([pwdRequest, documentRequest])
      .then(([pwdResult, documentResult]) => {
        const pwds = pwdResult.data?.instances || [];
        const documents = documentResult.data?.instances || [];

        const pwdEventsPromise = pwds.length > 0
          ? axios.get(`/tracker/events?program=${PWD_PROGRAM}&orgUnit=${districtId}&pageSize=5000`)
          : Promise.resolve({ data: { instances: [] } });
        const documentEventsPromise = documents.length > 0
          ? axios.get(`/tracker/events?program=${DOCUMENTS_HUB_PROGRAM}&orgUnit=${districtId}&startDate=${year}-01-01&endDate=${year}-12-31&pageSize=5000`)
          : Promise.resolve({ data: { instances: [] } });

        return Promise.all([pwdEventsPromise, documentEventsPromise]).then(([pwdEvents, documentEvents]) => {
          const reports = pwdEvents.data?.instances || [];
          const docReports = documentEvents.data?.instances || [];
          const pwdsUpToYear = pwds.filter((pwd) => isIncidentOnOrBeforeSelectedYear(pwd, year));
          const pwdTotal = pwdsUpToYear.length;
          let nhisTotal = 0;
          let igaTotal = 0;
          let cbTotal = 0;
          const listRows = [];

          pwdsUpToYear.forEach((pwd, index) => {
            const currentReport = reports.find(
              (report) => report.trackedEntity === pwd.trackedEntity && report.programStage === PWD_DETAIL_STAGE,
            );

            if (currentReport) {
              currentReport.dataValues.forEach((value) => {
                if (value.dataElement === NHIS_ELEMENT && value.value === "true") {
                  nhisTotal++;
                } else if (value.dataElement === IGA_ELEMENT && value.value === "true") {
                  igaTotal++;
                } else if (value.dataElement === CAPACITY_BUILDING_ELEMENT && value.value === "true") {
                  cbTotal++;
                }
              });
            }

            const firstName = getAttributeValue("First Name", pwd);
            const lastName = getAttributeValue("Last Name", pwd);

            listRows.push({
              key: pwd.trackedEntity || `pwda-${index}`,
              name: `${firstName} ${lastName}`.trim(),
              gender: getAttributeValue("Sex", pwd),
              incidentDate: getIncidentDate(pwd),
              nhisEnrolmentDate: getNhisEnrolmentDate(pwd, reports),
            });
          });

          const pwdPercentage = calculatePercentage(nhisTotal, pwdTotal);
          const igaPercentage = calculatePercentage(igaTotal, pwdTotal);
          const cbPercentage = calculatePercentage(cbTotal, pwdTotal);

          const pwdProgressDocs = formatDataGeneral(
            documents,
            "DPAT | M&E Tool Type",
            PWD_PROGRESS_REPORT_TYPE,
          ) || [];
          let progressReportLink = "Not Uploaded";
          let progressReportTitle = "Annual Progress Reports on PWDA";

          for (const document of pwdProgressDocs) {
            const eventId = getFileLinkIfExist(docReports, MONITORING_REPORT_FILE, document.trackedEntity);
            if (eventId) {
              progressReportTitle = getAttributeValue("Name", document);
              progressReportLink = buildFileUrl(eventId, MONITORING_REPORT_FILE);
              break;
            }
          }

          setPwdList(listRows);
          setProgressReport([{
            key: "pwda-progress-report",
            title: progressReportTitle,
            evidence: progressReportLink,
          }]);
          setPwdData({
            pwd: [{ key: "pwda-nhis-summary", pwdTotal, nhisTotal, percentage: pwdPercentage }],
            iga: [{ key: "pwda-iga-summary", pwdTotal, igaTotal, percentage: igaPercentage }],
            cb: [{ key: "pwda-cb-summary", pwdTotal, cbTotal, percentage: cbPercentage }],
          });
          setScorei(Number(pwdPercentage) >= 90 ? 1 : 0);
          setScoreii(Number(igaPercentage) >= 80 ? 2 : 0);
          setScoreiii(Number(cbPercentage) >= 30 ? 1 : 0);
        });
      })
      .catch((err) => console.log(err));
  }, [districtId, year]);

  useDpatIndicatorDataSource({
    isAssessmentStarted,
    savedAssessmentData,
    assessmentStartLoading,
    loadFromDhis2,
    applySnapshot: (snap) => {
      if (snap.pwdData) setPwdData(snap.pwdData);
      if (snap.pwdList) setPwdList(snap.pwdList);
      if (snap.progressReport) setProgressReport(snap.progressReport);
      if (snap.scorei != null) setScorei(snap.scorei);
      if (snap.scoreii != null) setScoreii(snap.scoreii);
      if (snap.scoreiii != null) setScoreiii(snap.scoreiii);
      if (snap.maxScore != null) setMaxScore(snap.maxScore);
    },
    deps: [year, districtId, loadFromDhis2],
  });

  const pwdNHISColumn = [
    { title: "a. Number of PWDAs registered with the District", dataIndex: "pwdTotal", key: "pwdTotal" },
    { title: "b. Number of PWDAs enrolled with NHIS", dataIndex: "nhisTotal", key: "nhisTotal" },
    { title: "c. % of PWDAs enrolled with NHIS (b/a X 100)", dataIndex: "percentage", key: "percentage" },
  ];

  const pwdIGAColumn = [
    { title: "a. Number of PWDAs registered with the District", dataIndex: "pwdTotal", key: "pwdTotal" },
    { title: "b. Number of PWDAs supported to engage in IGA", dataIndex: "igaTotal", key: "igaTotal" },
    { title: "c. % of PWDAs engaged in IGA (b/a X 100)", dataIndex: "percentage", key: "percentage" },
  ];

  const pwdCBColumn = [
    { title: "a. Number of PWDAs registered with the District", dataIndex: "pwdTotal", key: "pwdTotal" },
    { title: "b. Number of PWDAs whose capacities have been built", dataIndex: "cbTotal", key: "cbTotal" },
    { title: "c. % of PWDAs whose capacity has been built (b/a X 100)", dataIndex: "percentage", key: "percentage" },
  ];

  const pwdListColumn = useMemo(
    () => buildSearchableColumns(
      [
        { title: "Name", dataIndex: "name", key: "name" },
        { title: "Gender", dataIndex: "gender", key: "gender" },
        { title: "Incident Date", dataIndex: "incidentDate", key: "incidentDate" },
        { title: "NHIS Enrolment Date", dataIndex: "nhisEnrolmentDate", key: "nhisEnrolmentDate" },
      ],
      pwdList,
      {
        textSearchKeys: ["name", "incidentDate", "nhisEnrolmentDate"],
        selectFilterKeys: ["gender"],
      },
    ),
    [pwdList],
  );

  const progressReportColumn = [
    { title: "Report", dataIndex: "title", key: "title" },
    { title: "Evidence", dataIndex: "evidence", key: "evidence" },
  ];

  return (
    <Comment
      data={pwdData.pwd}
      year={year}
      districtId={districtId}
      tableCommentedId={`sdi4.0-4.3-${year}`}
      hideComment={hideComment}
    >
      {({ renderCommentFeedbackFooter }) => (
        <DpatSubIndicatorSection code={DPAT_SLUG}>
          <DpatIndicatorCmsIntro
            year={year}
            slug={DPAT_SLUG}
            dhis2ProgramId={PWD_PROGRAM}
            movDefaultText="People With Disability in the district."
          />

          <IndicatorScoreSection>
            <MaxScoreDisplay score={maxScore} />
            <ActualScoreDisplay slug={DPAT_SLUG} label="SDI 4.0-4.3i Actual Score:" score={scorei} />
            <ActualScoreDisplay slug={DPAT_SLUG} label="SDI 4.0-4.3ii Actual Score:" score={scoreii} />
            <ActualScoreDisplay slug={DPAT_SLUG} label="SDI 4.0-4.3iii Actual Score:" score={scoreiii} />
          </IndicatorScoreSection>

          <Title level={4} style={{ marginTop: "20px" }}>List of Registered PWDA</Title>
          <Table
            columns={pwdListColumn}
            dataSource={pwdList}
            pagination={getDpatTablePagination("PWDA")}
            bordered
            rowKey="key"
          />

          <Title level={4} style={{ marginTop: "20px" }}>I- Evidence of Registration of PWDAs with NHIS</Title>
          <Table
            columns={pwdNHISColumn}
            dataSource={pwdData.pwd || []}
            pagination={false}
            bordered
            rowKey="key"
          />

          <Title level={4} style={{ marginTop: "20px" }}>II- Evidence of PWDAs engaged in Inclusive IGA</Title>
          <Table
            columns={pwdIGAColumn}
            dataSource={pwdData.iga}
            pagination={false}
            bordered
            rowKey="key"
          />

          <Title level={4} style={{ marginTop: "20px" }}>III- Evidence of capacity building for PWDAs in a Vocation/skill</Title>
          <Table
            columns={pwdCBColumn}
            dataSource={pwdData.cb}
            pagination={false}
            bordered
            rowKey="key"
          />

          <Title level={4} style={{ marginTop: "20px" }}>IV- Progress Report</Title>
          <Table
            columns={progressReportColumn}
            dataSource={transformDpatTableRows(progressReport, ["evidence"], { evidence: "View Evidence" })}
            pagination={false}
            bordered
            rowKey="key"
          />
          {!hideComment && renderCommentFeedbackFooter()}
        </DpatSubIndicatorSection>
      )}
    </Comment>
  );
});

export default PWDService;
